import { useState } from "react";

import { ArrowDownTrayIcon, ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/outline";

import usePartAiCondition from "../../data/usePartAiCondition";
import { IPart, IPartAiConditionResult } from "../../models/Part";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";

interface AiConditionGeneratorProps {
  part: IPart;
  onApply: (conditionDescription: string) => void;
  disabled?: boolean;
}

/**
 * Generates a customer-facing condition description from the part's front and back photos, using
 * the damage prompt configured for its category in Wilma admin.
 *
 * Deliberately never applies on its own: the copy is written from photographs, so an operator
 * checks it against the observations before it reaches a listing.
 */
export default function AiConditionGenerator({ part, onApply, disabled = false }: AiConditionGeneratorProps) {
  const { generateAiCondition } = usePartAiCondition();
  const { addNotification } = useNotification();

  const [result, setResult] = useState<IPartAiConditionResult | null>(null);

  const handleGenerate = () => {
    generateAiCondition.mutate(part.id, {
      onSuccess: (response) => {
        if (response.success && response.data) {
          setResult(response.data);
        } else {
          addNotification({
            variant: "error",
            primaryText: response.message ?? "Unable to generate a condition description",
          });
        }
      },
      onError: (error: any) => {
        addNotification({
          variant: "error",
          primaryText: error?.message ?? "Unable to generate a condition description",
        });
      },
    });
  };

  const isGenerating = generateAiCondition.isLoading;

  // The API stores every generation on the part, so a description written on an earlier visit is
  // offered without spending another vision call.
  const stored = !result && part.damageDescription ? part.damageDescription : null;

  return (
    <>
      {!result && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={disabled || isGenerating}
            title="Describe this part's condition from its photos"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-violet-700 transition hover:bg-violet-50 disabled:opacity-60"
          >
            <SparklesIcon className={classNames("h-3.5 w-3.5", isGenerating && "animate-pulse")} />
            {isGenerating ? "Generating..." : "Generate AI Condition"}
          </button>

          {stored && (
            <button
              type="button"
              onClick={() => onApply(stored)}
              disabled={disabled}
              title="Use the description generated for this part earlier"
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-60"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              Use last generated
            </button>
          )}
        </div>
      )}

      {result && (
        <div className="w-full rounded-md border border-violet-200 bg-violet-50/40 p-2.5 text-xs text-gray-700">
          <div className="flex items-start gap-2">
            <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
            <span className="min-w-0 flex-1 whitespace-pre-wrap break-words font-medium text-gray-900">
              {result.conditionDescription}
            </span>
          </div>

          {result.observations.length > 0 && (
            <div className="mt-1.5 text-[11px] text-gray-600">
              <span className="font-semibold">Seen in the photos: </span>
              <ul className="mt-0.5 list-disc pl-4">
                {result.observations.map((observation) => (
                  <li key={observation}>{observation}</li>
                ))}
              </ul>
            </div>
          )}

          {result.warnings.length > 0 && (
            <p className="mt-1 text-[11px] text-amber-700">
              <span className="font-semibold">Warnings: </span>
              {result.warnings.join(", ")}
            </p>
          )}

          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onApply(result.conditionDescription);
                setResult(null);
              }}
              disabled={disabled}
              title="Use this as the listing's condition description"
              className="inline-flex items-center gap-1 rounded-md border border-violet-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-violet-700 transition hover:bg-violet-50 disabled:opacity-60"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              Use AI description
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={disabled || isGenerating}
              title="Generate a new condition description"
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-60"
            >
              <ArrowPathIcon className={classNames("h-3.5 w-3.5", isGenerating && "animate-spin")} />
              Regenerate
            </button>
          </div>
        </div>
      )}
    </>
  );
}
